import { FC, useCallback, Suspense, useState, useEffect } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";

import {
  Alert,
  AlertDescription,
  Button,
  type Key,
  Select,
  SelectItem,
  Surface,
  Tag,
  Text,
  ToggleSwitch,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";
import {
  togglesAtomResettable,
  stringFlagTreatmentsAtom_SUSPENDS,
} from "@zapier/canvas-state";

import styles from "./DevTools.module.css";

const STORAGE_KEY = "devToolsPinnedFeatureFlags";

const usePinnedFeatureFlags = () => {
  const [pinnedFlags, setPinnedFlags] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedFlags));
    } catch (error) {
      console.error(
        "Failed to save pinned feature flags to localStorage:",
        error,
      );
    }
  }, [pinnedFlags]);

  const togglePin = useCallback((flagName: string) => {
    setPinnedFlags((prev) =>
      prev.includes(flagName)
        ? prev.filter((name) => name !== flagName)
        : [...prev, flagName],
    );
  }, []);

  const isPinned = useCallback(
    (flagName: string) => pinnedFlags.includes(flagName),
    [pinnedFlags],
  );

  return { pinnedFlags, togglePin, isPinned };
};

const SuspendedFeatureFlagsSection: FC = () => {
  const [toggles, setToggles] = useAtom(togglesAtomResettable);
  const resetAllFeatureFlags = useResetAtom(togglesAtomResettable);
  const { togglePin, isPinned: checkIsPinned } = usePinnedFeatureFlags();

  // Use single atom for treatments
  const stringFlagTreatments = useAtomValue(stringFlagTreatmentsAtom_SUSPENDS);
  const togglesEntries = Object.entries(toggles);

  const handleResetAllFeatureFlags = useCallback(() => {
    resetAllFeatureFlags();
  }, [resetAllFeatureFlags]);

  const FeatureFlagRow: FC<{
    flagKey: string;
    flagValue: any;
    featureFlag: { name: string };
  }> = ({ flagKey, flagValue, featureFlag }) => {
    const isPinned = checkIsPinned(flagKey);

    return (
      <li className={styles.featureFlagRowWrapper}>
        <div className={styles.featureFlagContainer}>
          <div className={styles.featureFlagHeader}>
            <div className={styles.featureFlagNameRow}>
              <Text tag="div" type="SmallPrint3">
                {featureFlag.name}
              </Text>
              <Tooltip
                content={isPinned ? "Unpin" : "Pin to the top"}
                placement="top"
                offset="small"
              >
                <TooltipFocusableTrigger>
                  <button
                    data-pinned={isPinned}
                    className={styles.pinButton}
                    onClick={() => togglePin(flagKey)}
                    type="button"
                    aria-label={
                      isPinned ? "Unpin feature flag" : "Pin feature flag"
                    }
                  >
                    <Icon
                      name={isPinned ? "actionPinFill" : "actionPin"}
                      size={12}
                    />
                  </button>
                </TooltipFocusableTrigger>
              </Tooltip>
            </div>
            <Control flagKey={flagKey} flagValue={flagValue} />
          </div>
        </div>
      </li>
    );
  };

  const Control: FC<{ flagKey: string; flagValue: any }> = ({
    flagKey,
    flagValue,
  }) => {
    const isBooleanFlagWithConfig =
      typeof flagValue === "object" &&
      flagValue !== null &&
      "isOn" in flagValue;
    const isStringFlagWithConfig =
      typeof flagValue === "object" &&
      flagValue !== null &&
      "treatment" in flagValue;

    // If it's a string (not boolean), show as a dropdown if we have treatments
    if (typeof flagValue === "string") {
      const possibleTreatments = stringFlagTreatments[flagKey];

      // If we have treatments data for this flag, show as Select dropdown
      if (Array.isArray(possibleTreatments) && possibleTreatments.length > 1) {
        return (
          <Select
            label="Treatment"
            isLabelHidden={true}
            selectedKey={flagValue}
            onChange={(newValue: Key | null) => {
              if (newValue) {
                setToggles((prevToggles) => ({
                  ...prevToggles,
                  [flagKey]: newValue as string,
                }));
              }
            }}
          >
            {possibleTreatments.map((treatment) => (
              <SelectItem
                key={treatment}
                textValue={treatment}
                value={treatment}
              >
                {treatment}
              </SelectItem>
            ))}
          </Select>
        );
      }

      // Fallback to Tag if no treatments data
      return <Tag variant="GrayWarm3">{flagValue}</Tag>;
    }

    // If it's a flag with treatment object like { treatment: "v3", config: {...} }, show treatment as dropdown if we have treatments
    if (isStringFlagWithConfig) {
      const possibleTreatments = stringFlagTreatments[flagKey];
      const currentValue = (
        flagValue as { treatment: string; config?: Record<string, unknown> }
      ).treatment;

      // If we have treatments data for this flag, show as Select dropdown
      if (Array.isArray(possibleTreatments) && possibleTreatments.length > 1) {
        return (
          <Select
            label="Treatment"
            isLabelHidden={true}
            selectedKey={currentValue}
            onChange={(newValue: Key | null) => {
              if (newValue) {
                setToggles((prevToggles) => ({
                  ...prevToggles,
                  [flagKey]: {
                    ...(flagValue as object),
                    treatment: newValue as string,
                  },
                }));
              }
            }}
          >
            {possibleTreatments.map((treatment) => (
              <SelectItem key={treatment} value={treatment}>
                {treatment}
              </SelectItem>
            ))}
          </Select>
        );
      }

      // Fallback to Tag if no treatments data
      return <Tag variant="GrayWarm3">{currentValue}</Tag>;
    }

    // For boolean flags and flags with isOn, show toggle
    return (
      <ToggleSwitch
        checked={
          isBooleanFlagWithConfig
            ? (flagValue as { isOn: boolean }).isOn
            : Boolean(flagValue)
        }
        onChange={() => {
          if (isBooleanFlagWithConfig) {
            setToggles((prevToggles) => ({
              ...prevToggles,
              [flagKey]: {
                ...(flagValue as object),
                isOn: !(flagValue as { isOn: boolean }).isOn,
              },
            }));
          } else {
            // For simple boolean flags, just toggle the boolean value
            setToggles((prevToggles) => ({
              ...prevToggles,
              [flagKey]: !flagValue,
            }));
          }
        }}
      />
    );
  };

  const createFeatureFlagRows = () => {
    // Sort entries: pinned flags first, then unpinned
    const sortedEntries = [...togglesEntries].sort(([keyA], [keyB]) => {
      const aIsPinned = checkIsPinned(keyA);
      const bIsPinned = checkIsPinned(keyB);

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      return 0; // Keep original order for flags with same pinned status
    });

    return sortedEntries.map(([key, value]) => {
      const featureFlag = {
        name: key,
      };

      return (
        <FeatureFlagRow
          key={key}
          flagKey={key}
          flagValue={value}
          featureFlag={featureFlag}
        />
      );
    });
  };

  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert title="" status="info" variant="compact">
        <AlertDescription>
          <Text type="MinimalPrint3">
            Flags affect the current session only. Pin frequently used flags or
            reset all.
          </Text>
        </AlertDescription>
      </Alert>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.devToolsContentWrapper}>
            <ul className={styles.featureFlagsWrapper}>
              {createFeatureFlagRows()}
            </ul>
            <div className={styles.buttonWrapperAlignRight}>
              <Button size="compact" onPress={handleResetAllFeatureFlags}>
                Reset all
              </Button>
            </div>
          </div>
        </div>
      </Surface>
    </div>
  );
};

export const FeatureFlagsTabContent: FC = () => {
  return (
    <Suspense fallback={null}>
      <SuspendedFeatureFlagsSection />
    </Suspense>
  );
};
